/**
 * TonkTablesPanel — read-only list of every non-closed Tonk table for
 * admin oversight. Auto-refreshes every 15s.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, RefreshCw, Lock } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

const PhasePill = ({ phase }) => {
    const map = {
        waiting: { bg: '#39FF14', label: 'WAITING' },
        in_hand: { bg: '#FFD700', label: 'IN HAND' },
        hand_ended: { bg: '#00FFFF', label: 'BETWEEN' },
        dealing: { bg: '#FF007F', label: 'DEALING' },
        resolving: { bg: '#FF6B00', label: 'RESOLVING' },
    };
    const p = map[phase] || { bg: '#A0AAB2', label: (phase || '').toUpperCase() };
    return (
        <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black"
            style={{ background: `${p.bg}22`, color: p.bg }}
        >
            {p.label}
        </span>
    );
};

const TonkTablesPanel = () => {
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/tonk/admin/tables`, { withCredentials: true });
            setTables(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load tables');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 15_000);
        return () => clearInterval(id);
    }, [load]);

    return (
        <div className="space-y-3" data-testid="tonk-tables-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#39FF14]" />
                    <div>
                        <p className="text-lg font-black text-white">Live Tonk tables</p>
                        <p className="text-xs text-white/50">{tables.length} active · auto-refreshes every 15s</p>
                    </div>
                </div>
                <Button variant="ghost" onClick={load} data-testid="tonk-tables-refresh">
                    <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0e0e0e] overflow-hidden">
                {loading ? (
                    <div className="py-16 flex justify-center"><div className="spinner" /></div>
                ) : tables.length === 0 ? (
                    <div className="py-10 px-4 text-center text-white/50 text-sm">
                        No active Tonk tables right now.
                    </div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {tables.map((t) => (
                            <div key={t.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`tonk-table-row-${t.id}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm font-bold text-white truncate">{t.name}</p>
                                        {t.is_private && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 text-[10px] text-white/70 px-1.5 py-0.5">
                                                <Lock className="w-3 h-3" /> {t.join_code || 'PRIV'}
                                            </span>
                                        )}
                                        <PhasePill phase={t.phase} />
                                    </div>
                                    <p className="text-[11px] text-white/40 mt-0.5 font-mono">{t.id}</p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-[10px] uppercase tracking-widest text-white/50">Seats</p>
                                    <p className="text-sm font-black text-white tabular-nums">
                                        {t.seats_taken}/{t.seats_total}
                                    </p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-[10px] uppercase tracking-widest text-white/50">Mode</p>
                                    <p className="text-sm font-bold text-white uppercase">{t.mode}</p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-[10px] uppercase tracking-widest text-white/50">Entry</p>
                                    <p className="text-sm font-bold text-[#FFD700] tabular-nums">${Number(t.entry_amount).toFixed(2)}</p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-[10px] uppercase tracking-widest text-white/50">Hand</p>
                                    <p className="text-sm font-bold text-white tabular-nums">#{t.hand_number}</p>
                                </div>
                                <div className="text-right shrink-0 min-w-[120px]">
                                    <p className="text-[10px] uppercase tracking-widest text-white/50">Updated</p>
                                    <p className="text-[11px] text-white/70">{fmtDate(t.updated_at)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TonkTablesPanel;
